import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';
import { AI_CLINIC_QUERY_KEYS } from './queries.js';

export interface ToggleModuleInput {
  clinicId: string;
  moduleKey: string;
  enabled: boolean;
  reason?: string | null;
}

/** Liga/desliga um módulo de IA para a clínica via RPC
 * `admin_toggle_clinic_module`. */
export function useToggleClinicModule(): UseMutationResult<void, Error, ToggleModuleInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicId, moduleKey, enabled, reason }) => {
      const { error } = await supabase.rpc(
        'admin_toggle_clinic_module' as never,
        {
          p_clinic_id: clinicId,
          p_module_key: moduleKey,
          p_enabled: enabled,
          p_reason: reason ?? null,
        } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: AI_CLINIC_QUERY_KEYS.modules(vars.clinicId) });
    },
  });
}

export interface SetByokInput {
  clinicId: string;
  providerId: string;
  apiKey: string;
  modelId?: string | null;
  nickname?: string | null;
}

/** Cria ou rotaciona a chave BYOK da clínica via RPC
 * `admin_set_clinic_byok` (cifra com pgcrypto + Vault no banco). */
export function useSetClinicByok(): UseMutationResult<string, Error, SetByokInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input): Promise<string> => {
      const { data, error } = await supabase.rpc(
        'admin_set_clinic_byok' as never,
        {
          p_clinic_id: input.clinicId,
          p_provider_id: input.providerId,
          p_plain_key: input.apiKey.trim(),
          p_nickname: input.nickname?.trim() ? input.nickname.trim() : null,
          p_model_id: input.modelId?.trim() ? input.modelId.trim() : null,
        } as never,
      );
      if (error) throw new Error(error.message);
      return data as unknown as string;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: AI_CLINIC_QUERY_KEYS.byok(vars.clinicId) });
    },
  });
}

/** Remove (soft-delete) a BYOK ativa da clínica. */
export function useDeleteClinicByok(): UseMutationResult<void, Error, { clinicId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicId }) => {
      const { error } = await supabase.rpc(
        'admin_delete_clinic_byok' as never,
        {
          p_clinic_id: clinicId,
        } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: AI_CLINIC_QUERY_KEYS.byok(vars.clinicId) });
    },
  });
}
